<template>
  <div class="cp-map" :style="{ height: `${height}px` }">
    <l-map ref="map" :zoom="6" :center="factory" :options="mapOptions" style="height: 100%; width: 100%" @ready="onReady">
      <!-- Repli sans WebGL / si le style OpenFreeMap ne charge pas : tuiles raster OpenStreetMap. -->
      <l-tile-layer v-if="useRaster" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; <a href=&quot;https://www.openstreetmap.org/copyright&quot; target=&quot;_blank&quot;>OpenStreetMap</a> contributors" />

      <!-- Trajets usine → livraison (arcs animés, couleur du statut) -->
      <l-polyline v-for="r in routes" :key="`r${r.id}`" :lat-lngs="r.path" :color="r.color" :weight="3" :opacity="0.9"
        dash-array="8 10" class-name="cp-route" />

      <!-- Départ : l'usine XYLO -->
      <l-marker :lat-lng="factory" :icon="factoryIcon" :z-index-offset="1000">
        <l-tooltip :options="{ direction: 'top', offset: [0, -14] }">XYLO · Teiu (RO)</l-tooltip>
      </l-marker>

      <!-- Livraisons -->
      <l-marker v-for="g in groups" :key="g.key" :lat-lng="g.latlng" :icon="g.icon">
        <l-popup :options="{ className: 'cp-map-popup', closeButton: false, minWidth: 240, maxWidth: 320, autoPanPaddingTopLeft: [60, 20] }">
          <div class="cp-pop">
            <div class="cp-pop-head">
              <img v-if="g.flag" :src="g.flag" height="14" alt="" />
              <b>{{ g.place }}</b>
            </div>
            <div v-for="it in g.items" :key="it.order.id" class="cp-pop-item">
              <div class="cp-pop-line">
                <span class="cp-pop-dot" :style="{ background: colorOf(it.order) }"></span>
                <b>{{ it.index }}. {{ isQuote(it.order) ? t('quotation') : t('order') }} #{{ it.order.id }}</b>
                <span class="cp-pop-chip ml-2" :style="{ background: colorOf(it.order) }">{{ statusLabel(it.order) }}</span>
              </div>
              <div class="cp-pop-line cp-pop-muted">
                {{ (it.order.totals && it.order.totals.volume ? it.order.totals.volume : 0).toFixed(2) }} m³
                <template v-if="it.order.date_delivery_desired"> · {{ t('requested_delivery') }} {{ date(it.order.date_delivery_desired) }}</template>
              </div>
              <div v-if="it.order.transport && it.order.transport.loading_date" class="cp-pop-line">
                🚚 #{{ it.order.transport.id }} · {{ date(it.order.transport.loading_date) }}<template v-if="it.order.transport.delivery_date"> ⟶ {{ date(it.order.transport.delivery_date) }}</template>
              </div>
              <a v-if="clickable" href="#" class="cp-pop-link" @click.prevent="$emit('open', it.order.id)">{{ t('details') }} →</a>
            </div>
          </div>
        </l-popup>
      </l-marker>

      <!-- Camions en route : posés sur le trajet au prorata des dates chargement → livraison -->
      <l-marker v-for="tk in trucks" :key="`t${tk.id}`" :lat-lng="tk.latlng" :icon="truckIcon" :z-index-offset="2000">
        <l-tooltip :options="{ direction: 'top', offset: [0, -12] }">#{{ tk.transport }} · {{ tk.pct }} %</l-tooltip>
      </l-marker>
    </l-map>

    <!-- Légende -->
    <div class="cp-map-legend">
      <div><span class="cp-pop-dot" :style="{ background: COLORS.ready }"></span>{{ t('ready') }}</div>
      <div><span class="cp-pop-dot" :style="{ background: COLORS.to_prepare }"></span>{{ t('to_prepare') }}</div>
      <div v-if="hasAwaiting"><span class="cp-pop-dot" :style="{ background: COLORS.awaiting }"></span>{{ t('awaiting_confirmation') }}</div>
      <div v-if="hasPending"><span class="cp-pop-dot" :style="{ background: COLORS.pending }"></span>{{ t('status_pending') }}</div>
      <div v-if="hasInvoiced"><span class="cp-pop-dot" :style="{ background: COLORS.invoiced }"></span>{{ t('status_invoiced') }}</div>
      <div v-if="trucks.length"><span class="cp-legend-truck">🚚</span>{{ t('on_the_way') }}</div>
    </div>
  </div>
</template>

<script>
// Carte des livraisons du portail client, open source : Leaflet + fond
// OpenFreeMap (style « liberty », sans clé ni quota) rendu par MapLibre, chargé
// à la demande depuis le CDN (webpack 4 n'empaquette pas maplibre-gl 4 ; même
// recette que views/customerfinder/CustomerFinder.vue) ; sans WebGL ou si le
// style échoue → tuiles raster OpenStreetMap.
// Usine XYLO (Teiu) → lieux de livraison en arcs animés, une pastille par lieu
// (numéros de la liste, couleur la plus urgente), bulle listant les commandes du
// lieu, camion posé sur le trajet quand le chargement est fait.
import L from 'leaflet'
import { LMap, LTileLayer, LMarker, LPopup, LTooltip, LPolyline } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { translate, formatDate } from '@/views/client/i18n.js'

const FACTORY = { lat: 45.895342, lng: 22.5019217 } // XYLO, Teiu (RO)
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty'
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js'
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css'
const MAPLIBRE_LEAFLET_JS = 'https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.22/leaflet-maplibre-gl.js'
const ATTRIBUTION = '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank">OpenMapTiles</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
const COLORS = { ready: '#28C76F', to_prepare: '#EA5455', awaiting: '#A0A6AD', pending: '#FF9F43', invoiced: '#00CFE8' }
const FLAGS = require.context('@/assets/images/flags', false, /\.png$/)

let maplibreReady = null

function loadScript (src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = resolve
    s.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(s)
  })
}

function loadMaplibre () {
  if (!maplibreReady) {
    if (!document.querySelector(`link[href="${MAPLIBRE_CSS}"]`)) {
      const l = document.createElement('link')
      l.rel = 'stylesheet'
      l.href = MAPLIBRE_CSS
      document.head.appendChild(l)
    }
    maplibreReady = (window.maplibregl ? Promise.resolve() : loadScript(MAPLIBRE_JS))
      .then(() => (window.L && window.L.maplibreGL ? null : loadScript(MAPLIBRE_LEAFLET_JS)))
      .catch((e) => { maplibreReady = null; throw e })
  }
  return maplibreReady
}

// Arc doux entre deux points (Bézier quadratique, sommet décalé de 18 % de la distance).
function arc (a, b, steps = 40) {
  const mx = (a.lat + b.lat) / 2
  const my = (a.lng + b.lng) / 2
  const dx = b.lat - a.lat
  const dy = b.lng - a.lng
  const c = { lat: mx - dy * 0.18, lng: my + dx * 0.18 }
  const pts = []
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const u = 1 - t
    pts.push([u * u * a.lat + 2 * u * t * c.lat + t * t * b.lat, u * u * a.lng + 2 * u * t * c.lng + t * t * b.lng])
  }
  return pts
}

function ymdToTime (ymd) {
  return ymd ? new Date(`${ymd}T12:00:00`).getTime() : null
}

export default {
  name: 'ClientDeliveryMap',
  components: { LMap, LTileLayer, LMarker, LPopup, LTooltip, LPolyline },
  inject: ['portal'],
  props: {
    orders: { type: Array, default: () => [] },
    height: { type: Number, default: 460 },
    clickable: { type: Boolean, default: true }
  },
  data () {
    return {
      COLORS,
      factory: [FACTORY.lat, FACTORY.lng],
      useRaster: false,
      mapOptions: { zoomControl: true, scrollWheelZoom: false, attributionControl: true }
    }
  },
  computed: {
    located () {
      return this.orders.filter(o => o.delivery && o.delivery.lat !== null && o.delivery.lng !== null)
    },
    hasAwaiting () {
      return this.located.some(o => o.awaiting_confirmation)
    },
    hasPending () {
      return this.located.some(o => o.status === 'pending')
    },
    hasInvoiced () {
      return this.located.some(o => o.status === 'invoiced')
    },
    // Une pastille par lieu de livraison (plusieurs commandes au même endroit,
    // fréquent) : numéros de la liste du tableau de bord, couleur la plus urgente.
    groups () {
      const map = new Map()
      this.orders.forEach((o, i) => {
        if (!o.delivery || o.delivery.lat === null || o.delivery.lng === null) return
        const key = `${o.delivery.lat.toFixed(4)},${o.delivery.lng.toFixed(4)}`
        if (!map.has(key)) {
          map.set(key, {
            key,
            latlng: [o.delivery.lat, o.delivery.lng],
            place: [o.delivery.place, o.delivery.postal_code].filter(Boolean).join(' ') || o.company,
            flag: this.flagSrc(o.delivery.country_code),
            items: []
          })
        }
        map.get(key).items.push({ order: o, index: i + 1 })
      })
      return Array.from(map.values()).map(g => {
        const color = this.groupColor(g.items.map(it => it.order))
        const label = g.items.length <= 3 ? g.items.map(it => it.index).join('·') : String(g.items.length)
        const small = label.length > 2 ? ' cp-pin-small' : ''
        return Object.assign(g, {
          color,
          icon: L.divIcon({
            className: 'cp-pin-wrap',
            html: `<div class="cp-pin${small}" style="background:${color}"><span>${label}</span></div>`,
            iconSize: [34, 42],
            iconAnchor: [17, 40],
            popupAnchor: [0, -36]
          })
        })
      })
    },
    routes () {
      return this.groups.map(g => ({
        id: g.key,
        color: g.color,
        path: arc(FACTORY, { lat: g.latlng[0], lng: g.latlng[1] })
      }))
    },
    // Camion en route (chargement passé, livraison estimée à venir), un par transport,
    // posé sur le trajet au prorata du temps écoulé.
    trucks () {
      const now = Date.now()
      const out = []
      const seen = new Set()
      for (const g of this.groups) {
        for (const { order } of g.items) {
          const tr = order.transport
          if (!tr || !tr.id || seen.has(tr.id) || !tr.loading_date || !tr.delivery_date) continue
          const a = ymdToTime(tr.loading_date)
          const b = ymdToTime(tr.delivery_date)
          if (!a || !b || b <= a || now < a || now >= b) continue
          seen.add(tr.id)
          const p = (now - a) / (b - a)
          const path = arc(FACTORY, { lat: g.latlng[0], lng: g.latlng[1] })
          out.push({ id: tr.id, transport: tr.id, pct: Math.round(p * 100), latlng: path[Math.min(path.length - 1, Math.round(p * (path.length - 1)))] })
        }
      }
      return out
    },
    factoryIcon () {
      return L.divIcon({
        className: 'cp-pin-wrap',
        html: '<div class="cp-factory">XYLO</div>',
        iconSize: [58, 26],
        iconAnchor: [29, 13]
      })
    },
    truckIcon () {
      return L.divIcon({ className: 'cp-pin-wrap', html: '<div class="cp-truck">🚚</div>', iconSize: [30, 30], iconAnchor: [15, 15] })
    }
  },
  watch: {
    orders () {
      this.$nextTick(() => this.fit())
    }
  },
  methods: {
    t (key, vars) { return translate(this.portal.lang, key, vars) },
    date (ymd) { return formatDate(this.portal.lang, ymd) },
    isQuote (o) { return o.status === 'pending' },
    colorOf (o) {
      if (o.status === 'pending') return COLORS.pending
      if (o.status === 'invoiced') return COLORS.invoiced
      if (o.awaiting_confirmation) return COLORS.awaiting
      return o.availability === 'to_prepare' ? COLORS.to_prepare : COLORS.ready
    },
    // Couleur la plus urgente du lieu : à préparer > en attente de validation > devis > prête.
    groupColor (orders) {
      const colors = orders.map(o => this.colorOf(o))
      for (const c of [COLORS.to_prepare, COLORS.awaiting, COLORS.pending, COLORS.invoiced]) if (colors.includes(c)) return c
      return COLORS.ready
    },
    flagSrc (cc) {
      const code = String(cc || '').toUpperCase() === 'GB' ? 'UK' : String(cc || '').toUpperCase()
      try { return FLAGS(`./${code}.png`) } catch (e) { return FLAGS('./UE.png') }
    },
    statusLabel (o) {
      if (o.status === 'pending') return this.t('status_pending')
      if (o.awaiting_confirmation) return this.t('awaiting_confirmation')
      if (o.status === 'invoiced') return this.t('status_invoiced')
      return o.availability === 'to_prepare' ? this.t('to_prepare') : this.t('ready')
    },
    map () {
      return this.$refs.map && this.$refs.map.mapObject
    },
    // Cadre : l'usine et toutes les livraisons (une seule livraison → zoom région).
    fit () {
      const map = this.map()
      if (!map) return
      map.invalidateSize()
      const pts = [this.factory].concat(this.groups.map(g => g.latlng))
      if (pts.length === 1) map.setView(this.factory, 6)
      else map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 9 })
    },
    async onReady () {
      setTimeout(() => this.fit(), 50)
      try {
        await loadMaplibre()
        const map = this.map()
        if (!map) return
        const layer = window.L.maplibreGL({ style: BASEMAP_STYLE, attribution: ATTRIBUTION }).addTo(map)
        const gl = layer.getMaplibreMap()
        let loaded = false
        gl.once('load', () => { loaded = true })
        gl.on('error', () => {
          if (loaded || this.useRaster) return
          map.removeLayer(layer)
          this.useRaster = true
        })
      } catch (e) {
        this.useRaster = true
      }
    }
  }
}
</script>

<style lang="scss">
/* Non scopé : Leaflet insère marqueurs et bulles hors de l'arbre du composant. */
.cp-map {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  .leaflet-container {
    background: #eef2f3;
    font-family: inherit;
  }
}
.cp-pin-wrap {
  background: transparent;
  border: none;
}
.cp-pin {
  width: 34px;
  height: 34px;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  span {
    transform: rotate(45deg);
    color: #fff;
    font-weight: 700;
    font-size: 13px;
  }
}
.cp-pin-small span {
  font-size: 10px;
  letter-spacing: -0.02em;
}
.cp-pop-item {
  padding: 6px 0;
  border-top: 1px solid #f0f0f0;
}
.cp-factory {
  padding: 4px 10px;
  border-radius: 13px;
  background: #1e3a2b;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-align: center;
  border: 2px solid #9ccfa4;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}
.cp-truck {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  animation: cp-truck-bob 1.6s ease-in-out infinite;
}
@keyframes cp-truck-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}
.cp-route {
  animation: cp-route-dash 1.4s linear infinite;
}
@keyframes cp-route-dash {
  to { stroke-dashoffset: -36; }
}
.cp-map-popup {
  .leaflet-popup-content-wrapper {
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }
  .leaflet-popup-content {
    margin: 12px 14px;
  }
}
.cp-pop {
  font-size: 13px;
  line-height: 1.5;
}
.cp-pop-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 14px;
}
.cp-pop-line {
  margin-top: 2px;
}
.cp-pop-muted {
  color: #888;
}
.cp-pop-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 50%;
}
.cp-pop-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.cp-pop-link {
  display: inline-block;
  margin-top: 6px;
  font-weight: 600;
}
.cp-map-legend {
  position: absolute;
  left: 12px;
  bottom: 22px;
  z-index: 500;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  line-height: 1.8;
}
.cp-legend-truck {
  margin-right: 6px;
}
@media (max-width: 576px) {
  .cp-map-legend {
    left: 8px;
    bottom: 18px;
    padding: 4px 8px;
    font-size: 10px;
    line-height: 1.6;
  }
}
</style>
